import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import {
  APP_INITIALIZER,
  Compiler,
  CompilerFactory,
  COMPILER_OPTIONS,
  NgModule,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { JitCompilerFactory } from "@angular/platform-browser-dynamic";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ngxLoadingAnimationTypes, NgxLoadingModule } from "ngx-loading";
import { ModalDialogModule } from "@preeco-privacy/ngx-modal-dialog";
import { ToastrModule } from "ngx-toastr";
import { AppComponent } from "./app.component";
import { DebugComponent } from "./debug.component";
import { GlobalsModule } from "./globals.module";
import { InterceptService } from "./intercept.service";
import { LoginModule } from "./login/login.module";
import { AppRoutingModule } from "./app-routing.module";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { ConfigService } from "./config.service";
import { UtilityModule } from "./utility.module";
import { MatDialogModule } from "@angular/material/dialog";
import { GuardManagementModule } from "./guards/guard-management.module";
import {
  GuardServiceClient,
  API_BASE_URL,
  SiteClient,
} from "../app/api/generated/ModulesAPIClient";
import { environment } from "../environments/environment";
import { patchShiftRecurrence } from "./api/api-model-patches";

export function createCompiler(compilerFactory: CompilerFactory) {
  return compilerFactory.createCompiler();
}

const appInitializerFn = (configService: ConfigService) => {
  return () => {
    return configService.setConfig();
  };
};

const configReaderFn = (configService: ConfigService) => {
  return configService.readConfig().ApiBaseUrl;
};

export function apiModelsPatchFactory() {
  return () => {
    patchShiftRecurrence();
  };
}

@NgModule({
  declarations: [AppComponent, DebugComponent],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    UtilityModule,
    BrowserAnimationsModule,
    MatIconModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatSidenavModule,
    MatExpansionModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
    MatListModule,
    NgxLoadingModule.forRoot({
      animationType: ngxLoadingAnimationTypes.circle,
      primaryColour: "#455A64",
      secondaryColour: "transparent",
      backdropBorderRadius: "3px",
      fullScreenBackdrop: true,
    }),
    ModalDialogModule.forRoot(),
    ToastrModule.forRoot({
      progressBar: false,
      closeButton: true,
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
      timeOut: 10000,
      extendedTimeOut: 120000,
      enableHtml: true,
    }),
    NgxMaterialTimepickerModule,
    LoginModule,
    GlobalsModule.forRoot(),
    GuardManagementModule,
    // routing module must be last, to avoid issues with lazy loading
    AppRoutingModule,
  ],
  providers: [
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [ConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: apiModelsPatchFactory,
      multi: true,
      deps: [],
    },
    MatIconRegistry,
    { provide: HTTP_INTERCEPTORS, useClass: InterceptService, multi: true },
    { provide: COMPILER_OPTIONS, useValue: {}, multi: true },
    {
      provide: CompilerFactory,
      useClass: JitCompilerFactory,
      deps: [COMPILER_OPTIONS],
    },
    { provide: Compiler, useFactory: createCompiler, deps: [CompilerFactory] },
    GuardServiceClient,
    SiteClient,
    {
      provide: API_BASE_URL,
      useFactory: configReaderFn,
      deps: [ConfigService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  public constructor(public matIconRegistry: MatIconRegistry) {
    matIconRegistry.registerFontClassAlias("fontawesome", "fa");
  }
}
